import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";

type UserType = "Bireysel" | "Emlak Ofisi";

export default function LoginPage() {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<UserType>("Bireysel");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    // Demo mode: allow any non-empty input for quick testing
    if (!email.trim()) e.email = "E-posta adresi zorunludur";
    if (!password) e.password = "Şifre zorunludur";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    // Simulate login delay
    setTimeout(() => {
      localStorage.setItem(
        "auth_user",
        JSON.stringify({ email, userType, name: email.split("@")[0] })
      );
      setLoading(false);
      navigate("/panelim");
    }, 900);
  };

  return (
    <div className="min-h-screen bg-surface-50">
      <Navbar />

      <main className="pt-24 md:pt-28 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-md mx-auto">
            {/* Header */}
            <div className="mb-8 text-center">
              <h1 className="text-2xl md:text-3xl font-bold text-surface-900 mb-2">
                Giriş Yap
              </h1>
              <p className="text-sm text-surface-500">
                Hesabınıza giriş yaparak ilanlarınızı yönetin
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-surface-200 shadow-soft overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b border-surface-100">
                {(["Bireysel", "Emlak Ofisi"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setUserType(t);
                      setErrors({});
                    }}
                    className={`flex-1 py-3.5 text-sm font-medium transition-colors relative ${
                      userType === t
                        ? "text-primary-600"
                        : "text-surface-400 hover:text-surface-600"
                    }`}
                  >
                    {t}
                    {userType === t && (
                      <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-500 rounded-full" />
                    )}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="p-5 md:p-8 space-y-5">
                {/* Email */}
                <div data-error={errors.email ? "true" : undefined}>
                  <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                    E-posta Adresi
                    <span className="text-red-500"> *</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-surface-400">
                      <i className="ri-mail-line" />
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email)
                          setErrors((p) => ({ ...p, email: "" }));
                      }}
                      placeholder="ornek@email.com"
                      className="w-full pl-10 pr-4 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                    />
                  </div>
                  {errors.email && (
                    <span className="text-xs text-red-500 mt-1 block">
                      {errors.email}
                    </span>
                  )}
                </div>

                {/* Password */}
                <div data-error={errors.password ? "true" : undefined}>
                  <label className="text-xs font-medium text-surface-600 mb-1.5 block">
                    Şifre
                    <span className="text-red-500"> *</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-surface-400">
                      <i className="ri-lock-line" />
                    </span>
                    <input
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password)
                          setErrors((p) => ({ ...p, password: "" }));
                      }}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-3 bg-surface-50 border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-surface-400 hover:text-surface-600"
                    >
                      <i
                        className={
                          showPass ? "ri-eye-off-line" : "ri-eye-line"
                        }
                      />
                    </button>
                  </div>
                  {errors.password && (
                    <span className="text-xs text-red-500 mt-1 block">
                      {errors.password}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-surface-300 text-primary-500 focus:ring-primary-200"
                    />
                    <span className="text-xs text-surface-600">
                      Beni hatırla
                    </span>
                  </label>
                  <Link
                    to="#"
                    className="text-xs text-primary-500 hover:underline"
                  >
                    Şifremi unuttum
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 text-white text-sm font-bold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-60"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />
                      Giriş yapılıyor...
                    </span>
                  ) : (
                    <>
                      Giriş Yap
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>

                <div className="relative flex items-center py-2">
                  <div className="flex-grow border-t border-surface-200" />
                  <span className="shrink-0 mx-3 text-xs text-surface-400">
                    veya
                  </span>
                  <div className="flex-grow border-t border-surface-200" />
                </div>

                <p className="text-center text-sm text-surface-600">
                  Hesabınız yok mu?{" "}
                  <Link
                    to="/kayit"
                    className="font-semibold text-primary-500 hover:underline"
                  >
                    Hemen Kaydolun
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}